import {reportIpmVisible, type IpmMetricTags, useIpmRenderTiming} from '@github-ui/ipm-telemetry'
import {Profiler, useEffect} from 'react'
import {BannerNudgeCardPartial, type BannerNudgeCardPartialProps} from '../banner-nudge-card/BannerNudgeCardPartial'
import {Stackable} from './Stackable'

export interface NudgeCardStackPartialProps {
  cards: BannerNudgeCardPartialProps[]
}

const STACK_TELEMETRY_TAGS = {
  package: 'in-product-targeting',
  component: 'NudgeCardStack',
  category: 'nudge_card_stack',
  nudge_id: 'nudge_card_stack',
  variant: 'stacked_cards',
} as const satisfies IpmMetricTags

// Thin partial wrapper that bridges the JSON-props world (Ruby
// `render_react_partial`) to the React-children world (`Stackable`). The
// underlying `Stackable` stays a generic stacker that takes any children; this
// wrapper just maps `cards` into `BannerNudgeCardPartial` elements.
export function NudgeCardStackPartial({cards}: NudgeCardStackPartialProps) {
  const renderTiming = useIpmRenderTiming(STACK_TELEMETRY_TAGS)
  const hasCards = (cards?.length ?? 0) > 0

  useEffect(() => {
    if (hasCards) {
      reportIpmVisible(STACK_TELEMETRY_TAGS)
    }
  }, [hasCards])

  if (!cards || cards.length === 0) {
    return null
  }

  const stack = (
    <Stackable telemetryTags={STACK_TELEMETRY_TAGS}>
      {cards.map(card => (
        <BannerNudgeCardPartial key={card.testSelector} {...card} />
      ))}
    </Stackable>
  )

  if (!renderTiming.isEnabled) {
    return stack
  }

  return (
    <Profiler id="NudgeCardStack" onRender={renderTiming.onRender}>
      {stack}
    </Profiler>
  )
}
